'use client';

/**
 * useScrollAnimations Hook
 * ========================
 *
 * Lightweight scroll animations using GSAP ScrollTrigger.
 * Now with performance-aware settings.
 *
 * CLASSES TO USE IN YOUR JSX:
 * - .hero-section, .hero-video - Hero parallax
 * - .parallax-section - Sections that fade up on scroll
 * - .stagger-container > .stagger-item - Cards that reveal one-by-one
 * - .stat-number[data-value="X"] - Numbers that count up
 *
 * PERFORMANCE OPTIMIZATIONS:
 * - Respects prefers-reduced-motion
 * - Can be disabled entirely via enabled prop
 * - Uses will-change sparingly
 * - Kills all triggers on unmount
 */

import { useEffect, useRef } from 'react';

// GSAP + ScrollTrigger (~50 KB gzipped) are loaded with dynamic import()
// inside the effect instead of a static import, so they are split into a
// separate chunk that downloads after hydration rather than blocking LCP.
type ScrollTriggerStatic = typeof import('gsap/ScrollTrigger').ScrollTrigger;

interface ScrollAnimationOptions {
  enabled?: boolean;
  enableParallax?: boolean;
}

export function useScrollAnimations(options: ScrollAnimationOptions = {}) {
  const { enabled = true, enableParallax = true } = options;
  const initialized = useRef(false);

  useEffect(() => {
    // Skip if disabled or already initialized
    if (!enabled || initialized.current) return;
    initialized.current = true;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // If user prefers reduced motion, don't initialize any animations
    if (prefersReducedMotion) {
      console.log('[ScrollAnimations] Disabled due to prefers-reduced-motion');
      return;
    }

    let cancelled = false;
    let scrollTrigger: ScrollTriggerStatic | null = null;

    const initAnimations = async () => {
      const [{ gsap }, { ScrollTrigger }] = await Promise.all([
        import('gsap'),
        import('gsap/ScrollTrigger'),
      ]);
      if (cancelled) return;
      gsap.registerPlugin(ScrollTrigger);
      scrollTrigger = ScrollTrigger;

      // Hero video parallax - only if enabled
      const heroVideo = document.querySelector('.hero-video');
      if (heroVideo && enableParallax) {
        gsap.to(heroVideo, {
          yPercent: 15,
          ease: 'none',
          scrollTrigger: {
            trigger: '.hero-section',
            start: 'top top',
            end: 'bottom top',
            scrub: 1, // Smoother scrub with 1 second smoothing
          },
        });
      }

      // Section reveals - simple Y movement only
      const parallaxSections = gsap.utils.toArray('.parallax-section');
      parallaxSections.forEach((section: unknown) => {
        gsap.from(section as Element, {
          y: 30,
          duration: 0.6,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: section as Element,
            start: 'top 90%',
            once: true, // Only trigger once for better perf
          },
        });
      });

      // Staggered card reveals - simple Y movement only
      const staggerContainers = gsap.utils.toArray('.stagger-container');
      staggerContainers.forEach((container: unknown) => {
        const cards = (container as Element).querySelectorAll('.stagger-item');
        if (cards.length > 0) {
          gsap.from(cards, {
            y: 20,
            duration: 0.5,
            stagger: 0.08,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: container as Element,
              start: 'top 90%',
              once: true, // Only trigger once
            },
          });
        }
      });

      // Stats counter - only for visible numbers
      const statNumbers = gsap.utils.toArray('.stat-number');
      statNumbers.forEach((stat: unknown) => {
        const element = stat as HTMLElement;
        const originalContent = element.textContent;
        const hasPlus = originalContent?.includes('+');

        gsap.from(element, {
          textContent: 0,
          duration: 1.5, // Faster
          ease: 'power1.out',
          snap: { textContent: 1 },
          scrollTrigger: {
            trigger: element,
            start: 'top 85%',
            once: true,
          },
          onUpdate: function() {
            if (hasPlus && !element.textContent?.includes('+')) {
              element.textContent = Math.round(parseFloat(element.textContent || '0')) + '+';
            }
          },
        });
      });

      ScrollTrigger.refresh();
    };

    // Delay initialization slightly to not block main thread
    const timeoutId = setTimeout(initAnimations, 200);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      scrollTrigger?.getAll().forEach(trigger => trigger.kill());
      initialized.current = false;
    };
  }, [enabled, enableParallax]);
}

export default useScrollAnimations;
